import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// Sets the app's alternate icon from an unlocked streak reward.
//
// Nothing here checks `this.enabled` before sending, and that is deliberate.
// The getter is false until the native bridge adapter is installed on this
// web view, and the reward screen is a modal (`context: modal` in
// path-configuration.json) — so every time it opens it gets a brand new web
// view whose Stimulus controllers connect before the adapter lands. Guarding
// on it returned before `send` and dropped the connect message with no retry,
// which left the screen never knowing which icon was already active: the
// button kept the label Rails rendered ("Set as app icon") even when that
// icon was live, and the reset link stayed hidden because `#refresh` only
// ever runs off a reply.
//
// `Bridge.send` already handles both cases correctly — it queues messages
// while the adapter is missing and replays them once it arrives, and it drops
// messages for components the platform does not support. Same as
// push_token_controller, which uses this exact connect+reply pattern.
export default class extends BridgeComponent {
  static component = "app-icon"
  static values = {
    name: String,
    labelSet: String,
    labelSelected: String,
    labelSoon: String
  }
  static targets = ["button", "label", "reset"]

  currentName = ""

  connect() {
    super.connect()
    this.send("connect", {}, (message) => this.#onMessage(message))
  }

  set(event) {
    event?.preventDefault?.()
    const name = this.nameValue || ""
    if (!name) return
    if (this.currentName === name) return
    // No optimistic write: the icon the system is actually wearing is native's
    // to report. setAlternateIconName can fail — iOS 26.1 has a regression
    // where it fails for everything until the device reboots — and painting
    // "Selected" up front stranded the button there, because `currentName`
    // then matched `nameValue` and the guard above swallowed every later tap.
    this.send("set", { name })
  }

  #onMessage(message) {
    if (message?.event !== "state") return
    this.currentName = message.data?.currentName || ""
    this.#refresh()
  }

  #refresh() {
    if (!this.hasButtonTarget || !this.hasLabelTarget) return
    const hasName = !!this.nameValue
    const isCurrent = hasName && this.currentName === this.nameValue
    const btn = this.buttonTarget

    if (!hasName) {
      btn.disabled = true
      btn.dataset.state = "soon"
      this.labelTarget.textContent = this.labelSoonValue
    } else if (isCurrent) {
      btn.disabled = true
      btn.dataset.state = "selected"
      this.labelTarget.textContent = this.labelSelectedValue
    } else {
      btn.disabled = false
      btn.dataset.state = "set"
      this.labelTarget.textContent = this.labelSetValue
    }

    if (this.hasResetTarget) {
      this.resetTarget.hidden = !isCurrent
    }
  }

  reset(event) {
    event?.preventDefault?.()
    if (!this.currentName) return
    this.send("set", { name: "" })
  }
}
